import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { g, p, pa, del, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Printer, Plus, Trash2, FileText, Copy } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/** Stampanti termiche di reparto: è la stampante a chiamare il nostro server, quindi nel locale
 *  non serve nessun computer acceso né programmi da installare. */
export default function Stampanti() {
  const { departments, user } = useApp();
  const [d, setD] = useState(null);
  const [dept, setDept] = useState("");
  const [nome, setNome] = useState("");

  const load = useCallback(async () => {
    try {
      setD(await g("/stampanti"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 20000);
    return () => clearInterval(i);
  }, [load]);

  useEffect(() => { if (!dept && departments?.length) setDept(departments[0]); }, [departments, dept]);

  const aggiungi = async () => {
    try {
      await p("/stampanti", { dept, name: nome.trim() || `Stampante ${dept}`, by: user?.name });
      setNome("");
      toast.success("Stampante aggiunta: ora copia l'indirizzo nella stampante");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const copia = (testo) => {
    navigator.clipboard?.writeText(testo);
    toast.success("Indirizzo copiato");
  };

  if (!d) return <p className="text-sm text-slate-500">Carico le stampanti…</p>;

  return (
    <div className="fade-up space-y-5" data-testid="stampanti-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Reparti</p>
        <h1 className="font-head text-2xl font-bold sm:text-3xl">Stampanti termiche</h1>
        <p className="mt-1 text-sm text-slate-500">
          La comanda esce su carta nel reparto giusto appena la mandi. Niente computer acceso: è la
          stampante a chiedere al server se c'è qualcosa da stampare.
        </p>
      </div>

      <Card className="p-4" data-testid="stampanti-nuova">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Collega una stampante</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {(departments || []).map((x) => (
            <button
              key={x}
              onClick={() => setDept(x)}
              data-testid={`stampante-dept-${x.toLowerCase().replace(/\s+/g, "-")}`}
              className={`h-10 rounded-md border px-3 text-sm font-semibold ${
                dept === x ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200"
              }`}
            >
              {x}
            </button>
          ))}
          <Input className="h-10 w-44" placeholder="Nome (facoltativo)" value={nome}
                 onChange={(e) => setNome(e.target.value)} data-testid="stampante-nome" />
          <Button className="h-10" onClick={aggiungi} disabled={!dept} data-testid="stampante-aggiungi-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Aggiungi
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {d.rows.map((s) => (
          <Card key={s.id} className="p-4" data-testid={`stampante-${s.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="flex items-center gap-2 font-semibold">
                  <Printer className="h-4 w-4 shrink-0" /> {s.name}
                </p>
                <p className="text-xs text-slate-500">
                  {s.dept} ·{" "}
                  <span className={s.online ? "font-semibold text-emerald-700" : "text-amber-700"}>
                    {s.online ? "collegata" : "non collegata"}
                  </span>
                  {s.in_coda > 0 && ` · ${s.in_coda} in coda`}
                </p>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" onClick={async () => {
                  await p(`/stampanti/${s.id}/prova`, {});
                  toast.success("Foglio di prova in coda: esce entro pochi secondi");
                  load();
                }} data-testid={`stampante-prova-${s.id}`}>
                  <FileText className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={async () => {
                  await pa(`/stampanti/${s.id}`, { active: !s.active });
                  load();
                }} data-testid={`stampante-attiva-${s.id}`}>
                  {s.active ? "Spegni" : "Accendi"}
                </Button>
                <Button size="sm" variant="outline" onClick={async () => {
                  await del(`/stampanti/${s.id}`);
                  load();
                }} data-testid={`stampante-togli-${s.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="mt-3 rounded-md bg-slate-50 p-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
                Indirizzo da scrivere nella stampante
              </p>
              <p className="mt-1 break-all font-mono text-[11px] text-slate-700" data-testid={`stampante-url-${s.id}`}>
                {API}/api/print/pull/{s.token}
              </p>
              <Button size="sm" variant="outline" className="mt-2"
                      onClick={() => copia(`${API}/api/print/pull/${s.token}`)}
                      data-testid={`stampante-copia-${s.id}`}>
                <Copy className="mr-1.5 h-3.5 w-3.5" /> Copia indirizzo
              </Button>
            </div>
          </Card>
        ))}
        {d.rows.length === 0 && (
          <p className="text-sm text-slate-500">
            Nessuna stampante collegata: il locale lavora dal monitor, come adesso.
          </p>
        )}
      </div>

      <Card className="p-4" data-testid="stampanti-istruzioni">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Come si collega (5 minuti)</p>
        <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm text-slate-600">
          <li>Attacca la stampante al router con il cavo di rete (o al Wi-Fi del locale).</li>
          <li>Apri il suo pannello di configurazione dal browser (l'indirizzo è sul foglio di prova della stampante).</li>
          <li>Vai su <b>ePOS-Print → Server Direct Print</b> e attiva la funzione.</li>
          <li>Incolla nel campo <b>URL</b> l'indirizzo copiato qui sopra e imposta l'intervallo su <b>3 secondi</b>.</li>
          <li>Salva, poi tocca il foglietto «prova di stampa» qui accanto: se esce la carta, è fatta.</li>
        </ol>
        <p className="mt-3 text-xs text-slate-500">
          Modelli adatti: <b>Epson TM-T20III Ethernet</b> (circa 180-220 €, la più economica che fa
          questo lavoro) oppure <b>Epson TM-m30III</b> (250-300 €, anche Wi-Fi e più compatta).
          Servono carta termica da 80 mm e una presa di corrente accanto alla postazione.
        </p>
      </Card>
    </div>
  );
}
